import { Component, OnInit } from '@angular/core';

import { CategoryDto, KeywordDto, SelectedSlotDto, FirstStepData, Language, AvailabilitySlotDto, LanguageCode, Division } from '../shared/model';

import { MainViewService } from './main-view.service';
import { SpinnerService } from '../shared/spinner/spinner.service';
import { SharedService } from '../shared/shared.service';
import { TranslatedNotificationService } from '../shared/translation/translated-notification.service';

import { forkJoin } from 'rxjs';
import 'rxjs/add/operator/switchMap';

@Component({
  selector: 'app-main-view',
  templateUrl: './main-view.component.html',
  styleUrls: ['./main-view.component.css']
})
export class MainViewComponent implements OnInit {
  isBannerActive: boolean = true;
  shouldShowStepFirstAndHalf: boolean;
  firstStepActive: boolean;
  firstAndHalfStepActive: boolean;
  secondStepActive: boolean;
  thirdStepActive: boolean;
  allCategories: Array<CategoryDto>;
  public allLanguages: Array<Language>;
  englishLanguage: Language = <Language>{};
  currentLanguage: Language = <Language>{};
  availabilitySlots: Array<AvailabilitySlotDto>;
  allKeywords: Array<KeywordDto>;
  secondStepValues: SelectedSlotDto = <SelectedSlotDto>{};
  firstStepValues: FirstStepData = <FirstStepData>{};
  shouldShowSupportInformation: boolean;

  constructor(
    private mainViewService: MainViewService,
    private spinnerService: SpinnerService,
    private sharedService: SharedService,
    private notificationService: TranslatedNotificationService) { }

  ngOnInit() {
    this.spinnerService.show('spinner');
    this.firstStepActive = true;
    this.firstAndHalfStepActive = false;
    this.secondStepActive = false;
    this.thirdStepActive = false;

    this.sharedService.getUserDetails().subscribe(
      data => {
        if (data.Division == Division.ThermalEurope) {
          this.shouldShowSupportInformation = true;
        }
      }
    );
    
    this.sharedService.hasUserSetLanguage()
      .subscribe(result => this.shouldShowStepFirstAndHalf = !result);

    this.sharedService.getLanguages().switchMap((result) => {
      this.allLanguages = result;
      this.englishLanguage = result.find(language => language.LanguageCode === LanguageCode[LanguageCode.en] || language.LanguageCode === LanguageCode.en);
      
      return forkJoin(
        this.sharedService.getLanguagesTranslations(),
        this.sharedService.getCategoryTranslations());
    }).subscribe();

    this.initializeData();
  }

  initializeData() {
    this.currentLanguage = this.sharedService.getCurrentLanguage();

    const sources = [
      this.mainViewService.getCategories(),
      this.mainViewService.getEnglishLanguage(),
      this.checkIfKeywordsNeeded() ? this.mainViewService.getKeywords(this.currentLanguage.LanguageCode) : null,
      this.sharedService.getCategoryTranslations(),
    ];

    forkJoin(
      sources
    ).subscribe(responseList => {
      this.allCategories = this.sharedService.translateCategories(responseList[0]);
      this.englishLanguage = responseList[1];
      this.sharedService.getLanguagesTranslations()
        .subscribe(() => {
          this.englishLanguage = this.sharedService.translateLanguage(this.englishLanguage);
          this.currentLanguage = this.sharedService.translateLanguage(this.currentLanguage);
        });
      if (responseList[2]) {
        this.allKeywords = responseList[2];
      }
      this.spinnerService.hide('spinner');
    },
    () => {
      this.spinnerService.hide('spinner');
      this.notificationService.showMsgError('error.loading_initial_data', 'Error while loading initial data.');
    });
  }

  checkIfKeywordsNeeded(): boolean {
    return this.allKeywords === undefined
      || (this.allKeywords[0] !== undefined
        && this.allKeywords[0].LanguageCode === this.currentLanguage.LanguageCode);
  }

  onFirstSubmit(data) {
    if (data && data.Submit) {
      this.spinnerService.show('spinner');
      this.setSelectedCategory(data);
      this.firstStepActive = !data.Submit;

      if (this.shouldShowStepFirstAndHalf) {
        this.firstAndHalfStepActive = data.Submit;
        this.secondStepActive = false;
        this.spinnerService.hide('spinner');
      }
      else {
        this.secondStepActive = data.Submit;
      }
      
      this.thirdStepActive = false;

      if (this.secondStepActive === true) {
        this.disableBanner();
        var element = document.getElementById('stepTwoContainer');
        setTimeout(() => {
          element.scrollIntoView({behavior: "smooth"});
        }, 500 );

        this.mainViewService.getAvailabilitySlots(this.firstStepValues.Category.Id, this.currentLanguage.Id)
          .subscribe(result => {
            this.availabilitySlots = result;
            this.spinnerService.hide('spinner');
          },
          () => {
            this.spinnerService.hide('spinner');
            this.notificationService.showMsgError('error.loading_slots', 'Error while loading availability slots');
          });
          
      }
    } else if (data && data.Submit === false) {
      this.secondStepActive = false;
    }
  }

  onFirstAndHalfSubmit(): void {
    this.spinnerService.show('spinner');
    this.shouldShowStepFirstAndHalf = false;
    this.firstAndHalfStepActive = false;
    this.secondStepActive = true;
    this.firstStepActive = false;
    this.thirdStepActive = false;

    if (this.secondStepActive === true) {
      this.disableBanner();
      var element = document.getElementById('stepTwoContainer');
      setTimeout(() => {
        element.scrollIntoView({ behavior: "smooth" });
      }, 500);

      this.mainViewService.getAvailabilitySlots(this.firstStepValues.Category.Id, this.currentLanguage.Id)
        .subscribe(result => {
          this.availabilitySlots = result;
          this.spinnerService.hide('spinner');
        },
        () => {
          this.spinnerService.hide('spinner');
          this.notificationService.showMsgError('error.loading_slots', 'Error while loading availability slots');
        });  
    }
  }

  onSecondSubmit(selectedSlotValues: SelectedSlotDto) {
    this.secondStepActive = false;
    this.firstStepActive = false;
    this.firstAndHalfStepActive = false;
    this.thirdStepActive = true;
    this.secondStepValues = selectedSlotValues;
  }

  onThirdSubmit(submit: boolean) {
    this.thirdStepActive = submit;
    this.firstStepActive = false;
    this.firstAndHalfStepActive = false;
    this.secondStepActive = false;
  }

  setSelectedCategory(data) {
    if (data.Submit) {
      this.firstStepValues = data;
    } else {
      this.firstStepValues = <FirstStepData>{};
    }
  }

  disableBanner() {
    this.isBannerActive = false;
  }
  enableBanner() {
    this.isBannerActive = true;
  }
}
